﻿<template>
  <div class="admin-view">
    <div class="view-header">
      <h2>Galleries</h2>
      <div class="toolbar">
        <input
          v-model="searchQuery"
          class="gv-search"
          type="search"
          placeholder="Search client, gallery, location…"
          @input="onSearchInput"
        />
        <button @click="showCreate = true" class="btn btn-primary">+ New Gallery</button>
      </div>
    </div>

    <p v-if="loadError" class="load-error">{{ loadError }}</p>
    <div v-if="loading" class="loading-state">Loading galleries…</div>
    <div v-else-if="galleries.length === 0 && !loadError" class="empty">
      {{ searchQuery ? 'No galleries match your search.' : 'No galleries yet.' }}
    </div>

    <div class="gv-grid-cards">
      <div v-for="gallery in galleries" :key="gallery.id" class="card gv-tile">
        <div
          class="gv-card-main"
          role="button"
          tabindex="0"
          title="View gallery"
          @click="openView(gallery)"
          @keydown.enter="openView(gallery)"
        >
          
          <div class="gv-tile-head">
            <strong class="gv-tile-name">{{ gallery.name }}</strong>
            <span :class="['tier-badge', `tier-${gallery.tier}`]">{{ tierLabel(gallery.tier) }}</span>
          </div>
          
          <small v-if="gallery.description" class="gv-tile-desc">{{ gallery.description }}</small>
          <div class="gv-tile-preview">
            <div class="gv-session" v-if="gallery.session_date || gallery.location_text">
              <p class="gv-tile-sub">{{ gallery.client_name }} · {{ gallery.image_count }} images</p>
              <span v-if="gallery.session_date" class="gv-meta">📅 {{ formatSessionDate(gallery.session_date) }}</span>
              <span v-if="gallery.location_text" class="gv-meta">📍 {{ gallery.location_text }}</span>
            </div>
            <PolaroidStack :gallery-id="gallery.id" class="gv-tile-stack" />
          </div>
        </div>
        <div class="card-actions gv-tile-actions" @click.stop>
          <button @click="openEdit(gallery)"    class="btn btn-outline btn-sm" title="Edit gallery">Edit</button>
          <button @click="openSelect(gallery)"  class="btn btn-outline btn-sm" title="Select images">Images</button>
          <button @click="openAccess(gallery)"  class="btn btn-outline btn-sm" title="Access &amp; sharing">Access</button>
        </div>
      </div>
    </div>

    <!-- ── Create Gallery Modal ── -->
    <div v-if="showCreate" class="modal" @click.self="showCreate = false">
      <div class="modal-content">
        <h3>New Gallery</h3>
        <form @submit.prevent="createGallery">
          <div class="form-group">
            <label>Client</label>
            <div class="gv-client-row">
              <div class="gv-client-search">
                <input
                  v-model="clientQuery"
                  class="gv-client-input"
                  :placeholder="selectedClientName || 'Search clients…'"
                  @focus="clientPickerOpen = true"
                  @input="clientPickerOpen = true"
                  @blur="closeClientPickerSoon"
                />
                <button
                  v-if="newGallery.clientId"
                  type="button"
                  class="gv-client-clear"
                  title="Clear"
                  @click="clearClient"
                >✕</button>
                <ul v-if="clientPickerOpen && clientResults.length" class="gv-client-results">
                  <li
                    v-for="c in clientResults"
                    :key="c.id"
                    :class="{ active: c.id === newGallery.clientId }"
                    @mousedown.prevent="pickClient(c)"
                  >{{ c.name }}<span v-if="c.email" class="gv-client-email">{{ c.email }}</span></li>
                </ul>
                <p v-else-if="clientPickerOpen && clientQuery.trim()" class="gv-client-noresult">
                  No match — use “+ New” to create.
                </p>
              </div>
              <button type="button" class="btn btn-outline btn-sm" @click="openQuickClient">+ New</button>
            </div>
          </div>
          <div class="form-group">
            <label>Gallery Name</label>
            <input v-model="newGallery.name" required />
          </div>
          <div class="form-group">
            <label>Description <span class="optional">(optional)</span></label>
            <textarea v-model="newGallery.description" rows="3"></textarea>
          </div>
          <div class="form-group">
            <label>Access Tier</label>
            <select v-model="newGallery.tier">
              <option value="proof">Proofs — thumbnails only</option>
              <option value="preview">Preview — full uploaded image, no download</option>
              <option value="delivery">Delivery — full image + download available</option>
            </select>
          </div>
          <div class="form-group">
            <label>Session Date <span class="optional">(optional)</span></label>
            <input v-model="newGallery.sessionDate" type="date" />
          </div>
          <div class="form-group">
            <label>Location <span class="optional">(optional)</span></label>
            <input v-model="newGallery.locationText" @input="detectCoords(newGallery)" placeholder="Address, GPS coords, or e.g. Downtown San Antonio" />
            <small v-if="newGallery.coordsHint" class="gv-coords-hint">📍 Coordinates detected: {{ newGallery.coordsHint }}</small>
          </div>
          <p v-if="modalError" class="error">{{ modalError }}</p>
          <div class="modal-actions">
            <button type="button" @click="showCreate = false" class="btn btn-outline">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{ saving ? 'Creating…' : 'Create' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- ── Quick Create Client Modal (from New Gallery) ── -->
    <div v-if="showQuickClient" class="modal gv-quick-modal" @click.self="showQuickClient = false">
      <div class="modal-content">
        <h3>New Client</h3>
        <form @submit.prevent="createQuickClient">
          <div class="form-group gv-grid-2">
            <div>
              <label>First Name <span class="optional">(optional)</span></label>
              <input v-model.trim="quickClient.first_name" maxlength="120" @input="syncQuickName" />
            </div>
            <div>
              <label>Last Name <span class="optional">(optional)</span></label>
              <input v-model.trim="quickClient.last_name" maxlength="120" @input="syncQuickName" />
            </div>
          </div>
          <div class="form-group">
            <label>Display Name</label>
            <input v-model.trim="quickClient.name" maxlength="255" @input="quickNameTouched = true" placeholder="Auto from first + last" />
          </div>
          <EmailInput v-model="quickClient.email" label="Email" :optional="true" @update:valid="quickEmailValid = $event" />
          <PhoneInput v-model="quickClient.phone" label="Phone" :optional="true" @update:valid="quickPhoneValid = $event" />
          <p class="gv-quick-hint">Need more fields? Add the rest later from the
            <RouterLink to="/clients" class="gv-link">Clients page</RouterLink>.</p>
          <p v-if="quickError" class="error">{{ quickError }}</p>
          <div class="modal-actions">
            <button type="button" @click="showQuickClient = false" class="btn btn-outline">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="quickSaving || !quickValid">
              {{ quickSaving ? 'Creating…' : 'Create & Select' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- ── Edit Gallery Modal ── -->
    <div v-if="showEdit" class="modal" @click.self="showEdit = false">
      <div class="modal-content">
        <h3>Edit Gallery</h3>
        <form @submit.prevent="saveEdit">
          <div class="form-group">
            <label>Client</label>
            <select v-model="editGallery.clientId" required>
              <option v-for="c in clients" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>Gallery Name</label>
            <input v-model="editGallery.name" required />
          </div>
          <div class="form-group">
            <label>Description <span class="optional">(optional)</span></label>
            <textarea v-model="editGallery.description" rows="3"></textarea>
          </div>
          <div class="form-group">
            <label>Access Tier</label>
            <select v-model="editGallery.tier">
              <option value="proof">Proofs — thumbnails only</option>
              <option value="preview">Preview — full uploaded image, no download</option>
              <option value="delivery">Delivery — full image + download available</option>
            </select>
          </div>
          <div class="form-group">
            <label>Session Date <span class="optional">(optional)</span></label>
            <input v-model="editGallery.sessionDate" type="date" />
          </div>
          <div class="form-group">
            <label>Location <span class="optional">(optional)</span></label>
            <input v-model="editGallery.locationText" @input="detectCoords(editGallery)" placeholder="Address, GPS coords, or e.g. Downtown San Antonio" />
            <small v-if="editGallery.coordsHint" class="gv-coords-hint">📍 Coordinates detected: {{ editGallery.coordsHint }}</small>
          </div>

          <!-- Download controls (delivery tier only) -->
          <template v-if="editGallery.tier === 'delivery'">
            <div class="download-section">
              <div class="download-status" v-if="downloadInfo">
                <span v-if="downloadInfo.enabled && !isExpired(downloadInfo.expires_at)" class="dl-active">
                  ✓ Downloads enabled · expires {{ formatDate(downloadInfo.expires_at) }}
                </span>
                <span v-else-if="downloadInfo.enabled && isExpired(downloadInfo.expires_at)" class="dl-expired">
                  ⚠ Download link expired
                </span>
                <span v-else class="dl-inactive">Downloads disabled</span>
                <span v-if="downloadInfo.built_at" class="dl-built">
                  · Zip built {{ formatDate(downloadInfo.built_at) }}
                </span>
              </div>
              <div class="download-controls">
                <div class="form-group">
                  <label>Expiry (days from now)</label>
                  <input v-model.number="downloadExpiry" type="number" min="1" max="365" style="width:100px" />
                </div>
                <label class="checkbox-label">
                  <input type="checkbox" v-model="downloadPrebuild" /> Pre-build zip now
                </label>
                <div class="download-btns">
                  <button type="button" class="btn btn-primary btn-sm" :disabled="downloadSaving" @click="enableDownload">
                    {{ downloadSaving ? 'Saving…' : downloadInfo?.enabled ? 'Renew Link' : 'Enable Downloads' }}
                  </button>
                  <button type="button" class="btn btn-outline btn-sm" v-if="downloadInfo?.enabled" :disabled="downloadSaving" @click="disableDownload">
                    Disable
                  </button>
                </div>
              </div>
            </div>
          </template>
          <p v-if="modalError" class="error">{{ modalError }}</p>
          <div class="modal-actions">
            <button type="button" @click="showEdit = false" class="btn btn-outline">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{ saving ? 'Saving…' : 'Save Changes' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- ── Select Images Modal (G4): choose shoot images into this gallery ── -->
    <div v-if="showSelect" class="modal" @click.self="showSelect = false">
      <div class="modal-content select-modal">
        <div class="manage-header">
          <h3>{{ selectGallery?.name }} — Images</h3>
          <span class="manage-hint">Click a shoot image to add it · drag chosen images to reorder · ✕ to remove</span>
        </div>

        <div class="select-body">
          <!-- Chosen images (ordered) -->
          <div class="chosen-col">
            <h4 class="select-col-head">In this gallery ({{ chosenImages.length }})</h4>
            <div class="reorder-grid" @dragover.prevent>
              <div v-for="(img, index) in chosenImages" :key="img.id"
                   class="reorder-item" :class="{ 'drag-target': chosenDragOver === index, dragging: chosenDragSource === index }"
                   draggable="true"
                   @dragstart="chosenDragSource = index"
                   @dragenter.prevent="onChosenDragEnter(index)"
                   @dragend="chosenDragSource = chosenDragOver = null">
                <img v-if="chosenThumb(img)" :src="chosenThumb(img)" :alt="img.display_name || ''" />
                <span v-else class="no-thumb-sm">no variant</span>
                <span class="reorder-num">{{ index + 1 }}</span>
                <button class="img-btn del-btn" title="Remove from gallery" @click.stop="removeChosen(index)">✕</button>
              </div>
              <p v-if="!chosenImages.length" class="empty">No images yet — click images from a shoot on the right.</p>
            </div>
          </div>

          <!-- Shoot picker -->
          <aside class="picker-col">
            <input v-model="shootQuery" @input="loadPickerShoots" placeholder="Search shoots…" class="gv-search picker-search" />
            <div v-for="s in pickerShoots" :key="s.id" class="picker-shoot">
              <button class="picker-head" :class="{ open: pickerOpenShoot === s.id }" @click="togglePickerShoot(s.id)">
                <span class="picker-caret">{{ pickerOpenShoot === s.id ? '▾' : '▸' }}</span>
                <span class="picker-name">{{ s.name }}</span>
                <span class="picker-count">{{ chosenFromShoot(s.id) }}/{{ s.image_count }}</span>
              </button>
              <div v-if="pickerOpenShoot === s.id" class="picker-grid">
                <figure v-for="img in (pickerShootImages[s.id] || [])" :key="img.id"
                        class="picker-fig" :class="{ chosen: isChosen(img.id) }"
                        :title="isChosen(img.id) ? 'Selected — click to remove' : 'Click to add'"
                        @click="toggleChosen(img)">
                  <img v-if="pickerThumb(img)" :src="pickerThumb(img)" :alt="''" loading="lazy" />
                  <span v-else class="no-thumb-sm">?</span>
                  <span v-if="isChosen(img.id)" class="picker-check">✓</span>
                </figure>
              </div>
            </div>
            <p v-if="!pickerShoots.length" class="empty">No shoots found.</p>
          </aside>
        </div>

        <p v-if="selectStatus" class="gv-meta">{{ selectStatus }}</p>
        <div class="modal-actions">
          <button type="button" @click="showSelect = false" class="btn btn-outline">Close</button>
          <button type="button" class="btn btn-primary" :disabled="selectSaving" @click="saveChosen">
            {{ selectSaving ? 'Saving…' : 'Save selection' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Access & Sharing Modal (G5) ── -->
    <ModalShell
      v-model:open="showAccess"
      :title="accessGallery ? `${accessGallery.name} — Access & Sharing` : 'Access & Sharing'"
      max-width="520px"
    >
      <div class="acc-block">
        <div class="acc-row">
          <div class="acc-row-text">
            <span class="acc-label">Client access</span>
            <span :class="['tier-badge', accessState === 'active' ? 'tier-delivery' : 'tier-proof']">
              {{ accessState === 'active' ? 'Active' : accessState === 'disabled' ? 'Disabled' : 'Expired' }}
            </span>
          </div>
          <button v-if="accessState === 'active'" class="btn btn-outline btn-sm" :disabled="accessSaving" @click="setAccess('disabled')">Disable</button>
          <button v-else class="btn btn-primary btn-sm" :disabled="accessSaving" @click="setAccess('active')">Enable</button>
        </div>
        <small class="acc-hint">Disabling instantly revokes the client's <code>/g/…</code> links (edge-enforced). You always retain admin access.</small>
      </div>

      <div class="acc-block">
        <label class="acc-share-toggle">
          <input type="checkbox" :checked="shareEnabled" :disabled="shareSaving" @change="toggleShare(($event.target as HTMLInputElement).checked)" />
          <span>
            <span class="acc-label">Read-only share link</span>
            <small class="acc-hint">A public view-only link for family/friends — no login required.</small>
          </span>
        </label>
        <div v-if="shareEnabled && shareUrl" class="share-link-row">
          <input :value="shareUrl" readonly class="gv-search" @focus="($event.target as HTMLInputElement).select()" />
          <button type="button" class="btn btn-outline btn-sm" @click="copyShare">{{ copied ? 'Copied!' : 'Copy' }}</button>
        </div>
      </div>

      <p v-if="accessError" class="error">{{ accessError }}</p>
      <div class="modal-actions">
        <button type="button" @click="showAccess = false" class="btn btn-outline">Close</button>
      </div>
    </ModalShell>

    <!-- ── Gallery View (G2): read-only info + thumbnail grid ── -->
    <GalleryViewer
      v-model:open="showView"
      :title="viewGallery ? `${viewGallery.name} — ${viewGallery.client_name || ''} · ${viewPhotos.length} images` : 'Gallery'"
      :loading="viewLoading"
      :photos="viewPhotos"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import { useAdminApi } from '@/composables/useAdminApi'
import ModalShell from '@/components/ModalShell.vue'
import GalleryViewer from '@/components/GalleryViewer.vue'
import PolaroidStack from '@/components/PolaroidStack.vue'
import PhoneInput from '@/components/PhoneInput.vue'
import EmailInput from '@/components/EmailInput.vue'

interface Client  { id: number; name: string; email?: string | null }
interface Gallery {
  id: number; client_id: number; client_name: string; name: string; description: string;
  tier: string; image_count: number;
  session_date?: string | null; location_text?: string | null;
  location_lat?: number | null; location_lng?: number | null;
}
interface ManageImage { id: number; filename: string; image_path: string; thumbnail_path: string; sort_order: number }
interface UploadItem  { name: string; file: File; status: 'pending' | 'uploading' | 'done' | 'error' }

const { apiFetch } = useAdminApi()

// Portfolio media served under this CloudFront prefix; variants store keys as
// portfolio-media/pt/<token>/<label>.<ext>.
const MEDIA_PREFIX = '/portfolio-media/'
const variantThumb = (img: any): string | undefined => {
  const vs = img?.variants || []
  const v = vs.find((x: any) => x.label === '600') || vs.find((x: any) => x.label === '900')
        || vs.find((x: any) => x.label === 'full') || vs[0]
  return v ? MEDIA_PREFIX + String(v.s3_key).replace(/^portfolio-media\//, '') : undefined
}

// ── Select Images modal (choose shoot images into a gallery) ──
const showSelect = ref(false)
const selectGallery = ref<any | null>(null)
const chosenImages = ref<any[]>([])
const selectSaving = ref(false)
const selectStatus = ref('')
const shootQuery = ref('')
const pickerShoots = ref<any[]>([])
const pickerOpenShoot = ref<number | null>(null)
const pickerShootImages = reactive<Record<number, any[]>>({})

const chosenThumb = variantThumb
const pickerThumb = variantThumb
const isChosen = (id: number) => chosenImages.value.some(i => i.id === id)

async function openSelect(gallery: any) {
  selectGallery.value = gallery
  chosenImages.value = []
  selectStatus.value = ''
  showSelect.value = true
  const detail = await (await apiFetch(`/admin/galleries/${gallery.id}`)).json()
  chosenImages.value = detail.gallery?.images || []
  await loadPickerShoots()
}
async function loadPickerShoots() {
  const q = shootQuery.value.trim() ? `?q=${encodeURIComponent(shootQuery.value.trim())}` : ''
  const res = await apiFetch(`/admin/shoots${q}`)
  pickerShoots.value = (await res.json()).shoots || []
}
async function togglePickerShoot(id: number) {
  pickerOpenShoot.value = pickerOpenShoot.value === id ? null : id
  if (pickerOpenShoot.value && !pickerShootImages[id]) {
    const res = await apiFetch(`/admin/shoots/${id}`)
    pickerShootImages[id] = (await res.json()).shoot?.images || []
  }
}
function addChosen(img: any) { if (!isChosen(img.id)) chosenImages.value.push(img) }
function removeChosen(i: number) { chosenImages.value.splice(i, 1) }
// Click an image in the picker to add it, or remove it if already chosen (G4).
function toggleChosen(img: any) {
  const i = chosenImages.value.findIndex(c => c.id === img.id)
  if (i === -1) chosenImages.value.push(img)
  else chosenImages.value.splice(i, 1)
}
// How many of a shoot's loaded images are already in this gallery (badge).
function chosenFromShoot(shootId: number): number {
  const imgs = pickerShootImages[shootId] || []
  return imgs.filter((img: any) => isChosen(img.id)).length
}
async function saveChosen() {
  if (!selectGallery.value) return
  selectSaving.value = true
  try {
    await apiFetch(`/admin/galleries/${selectGallery.value.id}/refs`, {
      method: 'PUT',
      body: JSON.stringify({ items: chosenImages.value.map(i => ({ imageId: i.id })) }),
    })
    selectStatus.value = 'Saved.'
    await loadGalleries()
    setTimeout(() => { selectStatus.value = '' }, 1500)
  } catch { selectStatus.value = 'Save failed — see console.' }
  finally { selectSaving.value = false }
}

// ── Access & Sharing modal ──
const showAccess = ref(false)
const accessGallery = ref<any | null>(null)
const accessState = ref<'active' | 'disabled' | 'expired'>('active')
const accessSaving = ref(false)
const accessError = ref('')
const shareEnabled = ref(false)
const shareToken = ref<string | null>(null)
const shareSaving = ref(false)
const copied = ref(false)

const shareUrl = ref('')
function computeShareUrl() {
  shareUrl.value = shareToken.value ? `${location.origin}/share/${shareToken.value}` : ''
}

async function openAccess(gallery: any) {
  accessGallery.value = gallery
  accessError.value = ''
  showAccess.value = true
  const detail = (await (await apiFetch(`/admin/galleries/${gallery.id}`)).json()).gallery
  accessState.value = detail.access_state || 'active'
  shareEnabled.value = !!detail.share_enabled
  shareToken.value = detail.share_token || null
  computeShareUrl()
}
async function setAccess(state: 'active' | 'disabled') {
  if (!accessGallery.value) return
  accessSaving.value = true; accessError.value = ''
  try {
    await apiFetch(`/admin/galleries/${accessGallery.value.id}/access`, {
      method: 'PUT', body: JSON.stringify({ state }),
    })
    accessState.value = state
  } catch { accessError.value = 'Failed to update access.' }
  finally { accessSaving.value = false }
}
async function toggleShare(enabled: boolean) {
  if (!accessGallery.value) return
  shareSaving.value = true; accessError.value = ''
  try {
    const res = await (await apiFetch(`/admin/galleries/${accessGallery.value.id}/share`, {
      method: 'PUT', body: JSON.stringify({ enabled }),
    })).json()
    shareEnabled.value = !!res.shareEnabled
    shareToken.value = res.shareToken || null
    computeShareUrl()
  } catch { accessError.value = 'Failed to update share link.' }
  finally { shareSaving.value = false }
}
function copyShare() {
  if (!shareUrl.value) return
  navigator.clipboard.writeText(shareUrl.value).then(() => {
    copied.value = true; setTimeout(() => { copied.value = false }, 1500)
  })
}

// Drag-reorder shared by the select modal.
const chosenDragSource = ref<number | null>(null)
const chosenDragOver = ref<number | null>(null)
function onChosenDragEnter(index: number) {
  if (chosenDragSource.value === null || chosenDragSource.value === index) { chosenDragOver.value = index; return }
  const arr = chosenImages.value
  const [moved] = arr.splice(chosenDragSource.value, 1)
  arr.splice(index, 0, moved)
  chosenDragSource.value = index
  chosenDragOver.value = index
}

// ── List + search ──────────────────────────────────────────────────────────
const galleries   = ref<Gallery[]>([])
const clients     = ref<Client[]>([])
const loadError   = ref('')
const loading     = ref(true)
const searchQuery = ref('')
let searchTimer: ReturnType<typeof setTimeout> | null = null

const loadGalleries = async () => {
  const params = searchQuery.value.trim() ? `?q=${encodeURIComponent(searchQuery.value.trim())}` : ''
  const res = await apiFetch(`/admin/galleries${params}`)
  const data = await res.json()
  if (res.ok) galleries.value = data.galleries
}

const onSearchInput = () => {
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(() => { loadGalleries() }, 250)
}

// ── Gallery view (G2): read-only info + thumbnail grid on row click ──────────
const showView = ref(false)
const viewGallery = ref<any | null>(null)
const viewLoading = ref(false)
const viewPhotos = ref<Array<{ id: number | string; s3_key: string; caption?: string }>>([])

async function openView(gallery: any) {
  viewGallery.value = gallery
  viewPhotos.value = []
  viewLoading.value = true
  showView.value = true
  try {
    const detail = (await (await apiFetch(`/admin/galleries/${gallery.id}`)).json()).gallery
    viewGallery.value = { ...gallery, ...detail }
    const imgs = detail?.images || []
    viewPhotos.value = imgs
      .map((img: any) => {
        const key = variantThumb(img)
        return key ? { id: img.id, s3_key: key, caption: img.display_name || '' } : null
      })
      .filter(Boolean) as Array<{ id: number; s3_key: string; caption?: string }>
  } catch { /* viewer shows empty state */ }
  finally { viewLoading.value = false }
}

// ── Coord detection: "29.42, -98.49" → structured lat/lng ────────────────────
const COORD_RE = /^\s*(-?\d{1,3}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)\s*$/
const detectCoords = (target: { locationText: string; coordsHint: string; locationLat: number | null; locationLng: number | null }) => {
  const m = target.locationText.match(COORD_RE)
  if (m) {
    const lat = parseFloat(m[1]); const lng = parseFloat(m[2])
    if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      target.locationLat = lat; target.locationLng = lng
      target.coordsHint = `${lat}, ${lng}`
      return
    }
  }
  target.locationLat = null; target.locationLng = null; target.coordsHint = ''
}

const formatSessionDate = (d: string) => {
  // d is a YYYY-MM-DD date string; render without timezone drift.
  const [y, m, day] = d.split('T')[0].split('-').map(Number)
  return new Date(y, m - 1, day).toLocaleDateString()
}

onMounted(async () => {
  loading.value = true
  try {
    const [gRes, cRes] = await Promise.all([apiFetch('/admin/galleries'), apiFetch('/admin/clients')])
    const gData = await gRes.json(); const cData = await cRes.json()
    if (gRes.ok) galleries.value = gData.galleries; else loadError.value = gData.error
    if (cRes.ok) clients.value = cData.clients
  } catch (e: any) { if (e.message !== 'Unauthorized') loadError.value = 'Failed to load galleries' }
  finally { loading.value = false }
})

// ── Create ───────────────────────────────────────────────────────────────────
const showCreate = ref(false)
const modalError = ref('')
const saving     = ref(false)
const newGallery = reactive({
  clientId: '' as number | '', name: '', description: '', tier: 'proof',
  sessionDate: '', locationText: '', locationLat: null as number | null, locationLng: null as number | null, coordsHint: '',
})

// ── Client combobox (G1): type-to-search over loaded clients ──
const clientQuery = ref('')
const clientPickerOpen = ref(false)
const clientResults = computed(() => {
  const q = clientQuery.value.trim().toLowerCase()
  const base = q
    ? clients.value.filter(c => c.name.toLowerCase().includes(q) || (c.email ?? '').toLowerCase().includes(q))
    : clients.value
  return base.slice(0, 20)
})
const selectedClientName = computed(() => clients.value.find(c => c.id === newGallery.clientId)?.name ?? '')
function pickClient(c: Client) {
  newGallery.clientId = c.id
  clientQuery.value = ''
  clientPickerOpen.value = false
}
function clearClient() {
  newGallery.clientId = ''
  clientQuery.value = ''
}
function closeClientPickerSoon() {
  // Delay so a mousedown pick on a result lands before the list unmounts.
  setTimeout(() => { clientPickerOpen.value = false }, 150)
}

const createGallery = async () => {
  saving.value = true; modalError.value = ''
  try {
    const res = await apiFetch('/admin/galleries', { method: 'POST', body: JSON.stringify({
      clientId: newGallery.clientId, name: newGallery.name, description: newGallery.description, tier: newGallery.tier,
      sessionDate: newGallery.sessionDate || null, locationText: newGallery.locationText || null,
      locationLat: newGallery.locationLat, locationLng: newGallery.locationLng,
    }) })
    const data = await res.json()
    if (!res.ok) { modalError.value = data.error; return }
    await loadGalleries()
    showCreate.value = false
    Object.assign(newGallery, {
      clientId: '', name: '', description: '', tier: 'proof',
      sessionDate: '', locationText: '', locationLat: null, locationLng: null, coordsHint: '',
    })
  } catch { modalError.value = 'Network error' }
  finally   { saving.value = false }
}

// ── Quick Create Client (inline from New Gallery) ──
const showQuickClient = ref(false)
const quickSaving = ref(false)
const quickError = ref('')
const quickNameTouched = ref(false)
const quickClient = reactive({ first_name: '', last_name: '', name: '', email: '', phone: '' })
// EmailInput + PhoneInput report validity; blank optional values are valid, a
// bad one blocks save (same contract as the Clients page).
const quickEmailValid = ref(true)
const quickPhoneValid = ref(true)

const quickValid = computed(() => quickEmailValid.value && quickPhoneValid.value)

const syncQuickName = () => {
  if (quickNameTouched.value) return
  quickClient.name = [quickClient.first_name, quickClient.last_name].filter(Boolean).join(' ').trim()
}

const openQuickClient = () => {
  Object.assign(quickClient, { first_name: '', last_name: '', name: '', email: '', phone: '' })
  quickNameTouched.value = false
  quickError.value = ''
  showQuickClient.value = true
}

const createQuickClient = async () => {
  if (!quickValid.value) {
    quickError.value = !quickPhoneValid.value ? 'Phone number format is invalid' : 'Enter a valid email address'
    return
  }
  quickSaving.value = true; quickError.value = ''
  try {
    const res = await apiFetch('/admin/clients', { method: 'POST', body: JSON.stringify({
      name: quickClient.name || undefined,
      first_name: quickClient.first_name || undefined,
      last_name: quickClient.last_name || undefined,
      email: quickClient.email || null,
      phone: quickClient.phone || undefined,
    }) })
    const data = await res.json()
    if (!res.ok) { quickError.value = data.error || 'Create failed'; return }
    // Refresh the client list and auto-select the new client in the gallery form.
    const cRes = await apiFetch('/admin/clients')
    const cData = await cRes.json()
    if (cRes.ok) clients.value = cData.clients
    const newId = data.client?.id ?? data.id
    if (newId != null) newGallery.clientId = newId
    showQuickClient.value = false
  } catch { quickError.value = 'Network error' }
  finally { quickSaving.value = false }
}

// ── Edit ─────────────────────────────────────────────────────────────────────
const showEdit    = ref(false)
const editId      = ref(0)
const editGallery = reactive({
  clientId: 0, name: '', description: '', tier: 'proof',
  sessionDate: '', locationText: '', locationLat: null as number | null, locationLng: null as number | null, coordsHint: '',
})

// Download management (edit modal, delivery tier)
const downloadInfo    = ref<any>(null)
const downloadExpiry  = ref(60)
const downloadPrebuild = ref(false)
const downloadSaving  = ref(false)

const openEdit = (gallery: Gallery) => {
  editId.value = gallery.id
  Object.assign(editGallery, {
    clientId: gallery.client_id, name: gallery.name, description: gallery.description ?? '', tier: gallery.tier ?? 'proof',
    sessionDate: gallery.session_date ? gallery.session_date.split('T')[0] : '',
    locationText: gallery.location_text ?? '',
    locationLat: gallery.location_lat ?? null, locationLng: gallery.location_lng ?? null,
    coordsHint: (gallery.location_lat != null && gallery.location_lng != null) ? `${gallery.location_lat}, ${gallery.location_lng}` : '',
  })
  modalError.value = ''
  downloadInfo.value = null
  // Load download status for delivery galleries
  if (gallery.tier === 'delivery') {
    apiFetch(`/admin/galleries/${gallery.id}/download`).then(r => r.json()).then(d => { downloadInfo.value = d.download })
  }
  showEdit.value = true
}

const saveEdit = async () => {
  saving.value = true; modalError.value = ''
  try {
    const res = await apiFetch(`/admin/galleries/${editId.value}`, {
      method: 'PATCH',
      body: JSON.stringify({
        name: editGallery.name, description: editGallery.description, clientId: editGallery.clientId, tier: editGallery.tier,
        sessionDate: editGallery.sessionDate || null, locationText: editGallery.locationText || null,
        locationLat: editGallery.locationLat, locationLng: editGallery.locationLng,
      }),
    })
    const data = await res.json()
    if (!res.ok) { modalError.value = data.error; return }
    await loadGalleries()
    showEdit.value = false
  } catch { modalError.value = 'Network error' }
  finally   { saving.value = false }
}

const isExpired = (dt: string | null) => dt ? new Date(dt) < new Date() : false

const enableDownload = async () => {
  downloadSaving.value = true
  try {
    const res = await apiFetch(`/admin/galleries/${editId.value}/download`, {
      method: 'POST',
      body: JSON.stringify({ expiryDays: downloadExpiry.value, prebuild: downloadPrebuild.value }),
    })
    const data = await res.json()
    if (res.ok) downloadInfo.value = { enabled: true, expires_at: data.expiresAt, built_at: downloadInfo.value?.built_at, zip_s3_key: downloadInfo.value?.zip_s3_key }
  } catch { /* ignore */ }
  finally { downloadSaving.value = false }
}

const disableDownload = async () => {
  downloadSaving.value = true
  try {
    await apiFetch(`/admin/galleries/${editId.value}/download`, { method: 'DELETE' })
    if (downloadInfo.value) downloadInfo.value.enabled = false
  } catch { /* ignore */ }
  finally { downloadSaving.value = false }
}

// ── Manage Photos (reorder + delete + set thumbnail) ─────────────────────────
const showManage      = ref(false)
const manageGallery   = ref<Gallery | null>(null)
const manageImages    = ref<ManageImage[]>([])
const manageLoading   = ref(false)
const manageSaving    = ref(false)
const manageError     = ref('')
const dragSourceIndex = ref<number | null>(null)
const dragOverIndex   = ref<number | null>(null)
const deleteConfirmId = ref<number | null>(null)
const deleting        = ref(false)

// Comment counts + panel state for Manage Photos modal
const manageCommentCounts  = ref<Record<number, number>>({})
const manageCommentImageId = ref<number | null>(null)
const manageComments       = ref<any[]>([])
const manageCommentsLoading = ref(false)

const openManage = async (gallery: Gallery) => {
  manageGallery.value  = gallery
  manageImages.value   = []
  manageError.value    = ''
  deleteConfirmId.value = null
  manageCommentCounts.value  = {}
  manageCommentImageId.value = null
  manageComments.value       = []
  manageLoading.value  = true
  showManage.value     = true
  try {
    const [orderRes, commentsRes] = await Promise.all([
      apiFetch(`/admin/galleries/${gallery.id}/order`),
      apiFetch(`/admin/galleries/${gallery.id}/comments`),
    ])
    const orderData = await orderRes.json()
    if (orderRes.ok) manageImages.value = orderData.images
    else manageError.value = orderData.error

    if (commentsRes.ok) {
      const commentsData = await commentsRes.json()
      // Response is grouped: [{ gallery_image_id, comments: [...] }]
      const counts: Record<number, number> = {}
      for (const group of (commentsData.comments ?? [])) {
        counts[group.gallery_image_id] = group.comments.length
      }
      manageCommentCounts.value = counts
    }
  } catch { manageError.value = 'Failed to load images' }
  finally   { manageLoading.value = false }
}

const openManageComments = async (img: ManageImage) => {
  // Toggle off if already open for this image
  if (manageCommentImageId.value === img.id) {
    manageCommentImageId.value = null
    return
  }
  manageCommentImageId.value = img.id
  manageComments.value = []
  manageCommentsLoading.value = true
  try {
    const res = await apiFetch(`/admin/galleries/${manageGallery.value!.id}/comments`)
    if (res.ok) {
      const data = await res.json()
      // Response is grouped: [{ gallery_image_id, comments: [...] }]
      const group = (data.comments ?? []).find((g: any) => g.gallery_image_id === img.id)
      manageComments.value = group?.comments ?? []
    }
  } catch { /* non-fatal */ }
  finally { manageCommentsLoading.value = false }
}

const closeManage = () => {
  if (!manageSaving.value) {
    showManage.value = false
    manageCommentImageId.value = null
    manageComments.value = []
  }
}

const onDragEnter = (index: number) => {
  if (dragSourceIndex.value === null || dragSourceIndex.value === index) return
  dragOverIndex.value = index
  const items = [...manageImages.value]
  const [moved] = items.splice(dragSourceIndex.value, 1)
  items.splice(index, 0, moved)
  manageImages.value   = items
  dragSourceIndex.value = index
}

const saveOrder = async () => {
  if (!manageGallery.value) return
  manageSaving.value = true; manageError.value = ''
  try {
    const res = await apiFetch(`/admin/galleries/${manageGallery.value.id}/order`, {
      method: 'PATCH',
      body: JSON.stringify({ imageIds: manageImages.value.map(i => i.id) }),
    })
    if (!res.ok) { const d = await res.json(); manageError.value = d.error; return }
    showManage.value = false
  } catch { manageError.value = 'Network error' }
  finally   { manageSaving.value = false }
}

const confirmDelete = (img: ManageImage) => {
  deleteConfirmId.value = deleteConfirmId.value === img.id ? null : img.id
}

const deleteImage = async (img: ManageImage) => {
  if (!manageGallery.value) return
  deleting.value = true; manageError.value = ''
  try {
    const res = await apiFetch(`/admin/galleries/${manageGallery.value.id}/images/${img.id}`, { method: 'DELETE' })
    if (!res.ok) { const d = await res.json(); manageError.value = d.error; return }
    manageImages.value = manageImages.value.filter(i => i.id !== img.id)
    deleteConfirmId.value = null
    await loadGalleries()
  } catch { manageError.value = 'Network error' }
  finally   { deleting.value = false }
}

// ── Set Thumbnail ─────────────────────────────────────────────────────────────
const showSetThumb  = ref(false)
const thumbTargetImg = ref<ManageImage | null>(null)
const thumbFile     = ref<File | null>(null)
const thumbDragging = ref(false)
const thumbUploading = ref(false)
const thumbError    = ref('')
const thumbInput    = ref<HTMLInputElement | null>(null)

const openSetThumbnail = (img: ManageImage) => {
  thumbTargetImg.value = img
  thumbFile.value  = null
  thumbError.value = ''
  showSetThumb.value = true
}

const onThumbFileChange = (e: Event) => {
  const input = e.target as HTMLInputElement
  if (input.files?.[0]) thumbFile.value = input.files[0]
}

const onThumbDrop = (e: DragEvent) => {
  thumbDragging.value = false
  const f = e.dataTransfer?.files[0]
  if (f && f.type.startsWith('image/')) thumbFile.value = f
}

const uploadThumbnail = async () => {
  if (!thumbFile.value || !thumbTargetImg.value || !manageGallery.value) return
  thumbUploading.value = true; thumbError.value = ''
  const galleryId = manageGallery.value.id
  const imageId   = thumbTargetImg.value.id
  try {
    // Get presigned URL with type=thumbnail so key lands in /thumbnails/ sub-folder
    const urlRes = await apiFetch(
      `/admin/galleries/${galleryId}/upload-url?type=thumbnail`,
      { method: 'POST', body: JSON.stringify({ filename: thumbFile.value.name, contentType: thumbFile.value.type }) }
    )
    if (!urlRes.ok) throw new Error('Failed to get upload URL')
    const { uploadUrl, s3Key } = await urlRes.json()

    const putRes = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': thumbFile.value.type }, body: thumbFile.value })
    if (!putRes.ok) throw new Error('S3 upload failed')

    const patchRes = await apiFetch(`/admin/galleries/${galleryId}/images/${imageId}`, {
      method: 'PATCH',
      body: JSON.stringify({ s3Key }),
    })
    if (!patchRes.ok) throw new Error('Failed to update thumbnail')

    // Update local state so the grid refreshes immediately
    const idx = manageImages.value.findIndex(i => i.id === imageId)
    if (idx !== -1) manageImages.value[idx].thumbnail_path = `/gallery-images/${s3Key}`

    showSetThumb.value = false
  } catch (err: any) {
    thumbError.value = err.message
  } finally {
    thumbUploading.value = false
  }
}

// ── Upload Images ─────────────────────────────────────────────────────────────
const showUpload      = ref(false)
const uploadGallery   = ref<Gallery | null>(null)
const pendingFiles    = ref<File[]>([])
const uploadItems     = ref<UploadItem[]>([])
const uploading       = ref(false)
const uploadDoneCount = ref(0)
const uploadError     = ref('')
const isDragging      = ref(false)
const fileInput       = ref<HTMLInputElement | null>(null)
const uploadBuildZip  = ref(false)

const openUpload = (gallery: Gallery) => {
  uploadGallery.value = gallery; pendingFiles.value = []; uploadItems.value = []
  uploadError.value = ''; uploading.value = false; uploadDoneCount.value = 0
  uploadBuildZip.value = false
  showUpload.value = true
}

const closeUpload = () => { if (!uploading.value) showUpload.value = false }

const addFiles = (files: FileList | File[]) => {
  const images = Array.from(files).filter(f => f.type.startsWith('image/'))
  pendingFiles.value = [...pendingFiles.value, ...images]
  uploadItems.value  = pendingFiles.value.map(f => ({ name: f.name, file: f, status: 'pending' as const }))
}

const onFileChange = (e: Event) => { const i = e.target as HTMLInputElement; if (i.files) addFiles(i.files) }
const onDrop = (e: DragEvent) => { isDragging.value = false; if (e.dataTransfer?.files) addFiles(e.dataTransfer.files) }

const startUpload = async () => {
  if (!uploadGallery.value || !pendingFiles.value.length) return
  uploading.value = true; uploadError.value = ''; uploadDoneCount.value = 0
  const galleryId = uploadGallery.value.id
  for (const item of uploadItems.value) {
    item.status = 'uploading'
    try {
      const urlRes = await apiFetch(`/admin/galleries/${galleryId}/upload-url`,
        { method: 'POST', body: JSON.stringify({ filename: item.file.name, contentType: item.file.type }) })
      if (!urlRes.ok) throw new Error('Failed to get upload URL')
      const { uploadUrl, s3Key } = await urlRes.json()
      const putRes = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': item.file.type }, body: item.file })
      if (!putRes.ok) throw new Error('S3 upload failed')
      const recRes = await apiFetch(`/admin/galleries/${galleryId}/images`,
        { method: 'POST', body: JSON.stringify({ s3Key, filename: item.file.name, fileSize: item.file.size }) })
      if (!recRes.ok) throw new Error('Failed to record image')
      item.status = 'done'; uploadDoneCount.value++
    } catch (err: any) { item.status = 'error'; uploadError.value = `Error uploading ${item.name}: ${err.message}` }
  }
  uploading.value = false
  await loadGalleries()
  // If the gallery is delivery tier and the checkbox was checked, trigger zip build
  if (uploadBuildZip.value && uploadGallery.value?.tier === 'delivery') {
    const allDone = uploadItems.value.every(i => i.status === 'done')
    if (allDone) {
      try {
        await apiFetch(`/admin/galleries/${galleryId}/download`, {
          method: 'POST',
          body: JSON.stringify({ prebuild: true }),
        })
      } catch { /* non-fatal — zip build failure shouldn't block the upload success */ }
    }
  }
}

const tierLabel = (tier: string) =>
  ({ proof: 'Proofs', preview: 'Preview', delivery: 'Delivery' }[tier] ?? tier)

const { formatDate } = useAdminApi()
</script>

<style scoped>
/* ── Search + session metadata ── */
.gv-search {
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-sm);
  background: var(--bg-input);
  color: var(--text-primary);
  font-size: 0.875rem;
  min-width: 260px;
}
.gv-search:focus { outline: none; border-color: var(--border-focus); }
.gv-session { display: flex; gap: 0.85rem; flex-wrap: wrap; margin-top: 0.35rem; }
.gv-meta { color: var(--text-secondary); font-size: 0.78rem; }

/* Session metadata and polaroid stack side-by-side to save vertical space */
.gv-tile-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.35rem;
}
.gv-tile-preview .gv-session {
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 0.2rem;
  margin-top: 0;
}
.gv-tile-preview .gv-tile-stack {
  flex: 0 0 auto;
  width: 124px;
}
/* The stack's internal .pstack defaults to width:100%; cap it to the stack's
   intrinsic footprint (84px frame fanned ±20px ≈ 124px) so it's only as wide
   as needed and the session info keeps the rest of the row. */
.gv-tile-preview .gv-tile-stack :deep(.pstack) { width: 124px; }
.gv-coords-hint { color: var(--status-converted-text); font-size: 0.75rem; margin-top: 0.25rem; display: block; }

/* Inline quick-create-client (New Gallery modal) */
.gv-client-row { display: flex; gap: 0.5rem; align-items: stretch; }
.gv-client-row select { flex: 1; min-width: 0; }
.gv-client-row .btn-sm { white-space: nowrap; flex-shrink: 0; }
.gv-quick-modal .modal-content { max-width: 460px; }
.gv-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }
.gv-grid-2 > div { display: flex; flex-direction: column; }
.gv-quick-hint { font-size: 0.8rem; color: var(--text-secondary); margin: 0.25rem 0 0.5rem; }
.gv-link { color: var(--accent, var(--status-converted-text)); text-decoration: underline; }

/* ── Manage Photos modal header ── */
.manage-header { margin-bottom: 0.5rem; }
.manage-header h3 { margin: 0 0 0.2rem; font-size: 1.15rem; }
.manage-hint { color: var(--text-muted); font-size: 0.8rem; }

/* Two-column layout: image grid + optional comments panel */
.manage-body     { display: flex; gap: 1rem; min-height: 0; }
.manage-grid-col { flex: 1; min-width: 0; overflow-y: auto; max-height: 60vh; }

/* Comments side panel */
.manage-comments-panel {
  width: 240px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: var(--bg-surface);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-sm);
  padding: 0.75rem;
  max-height: 60vh;
  overflow-y: auto;
}
.manage-comments-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.6rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-primary);
}
.manage-comments-close {
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  font-size: 0.85rem;
  padding: 2px 4px;
  line-height: 1;
}
.manage-comments-close:hover { color: var(--text-primary); }
.manage-comments-loading,
.manage-comments-empty { font-size: 0.8rem; color: var(--text-muted); }
.manage-comments-list { display: flex; flex-direction: column; gap: 0.65rem; }
.manage-comment-row {
  background: var(--bg-surface-hover);
  border-radius: var(--radius-sm);
  padding: 0.45rem 0.55rem;
}
.manage-comment-meta {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.2rem;
  gap: 0.25rem;
  flex-wrap: wrap;
}
.manage-comment-author { font-size: 0.75rem; font-weight: 600; color: var(--accent-text); }
.manage-comment-date   { font-size: 0.68rem; color: var(--text-muted); }
.manage-comment-body   { margin: 0; font-size: 0.8rem; color: var(--text-secondary); line-height: 1.4; word-break: break-word; }

/* Comment count badge on thumbnail */
.manage-comment-badge {
  position: absolute;
  bottom: 3px;
  left: 5px;
  background: var(--accent);
  color: var(--text-primary);
  border-radius: 10px;
  font-size: 0.62rem;
  font-weight: 700;
  padding: 1px 5px;
  pointer-events: none;
  line-height: 1.4;
}

/* Highlight active comment image */
.reorder-item.comment-active { outline: 2px solid var(--accent); }

/* Widen modal when comments panel is open */
.reorder-modal.has-comments-panel { max-width: 960px; }

/* Image action buttons (appear on hover) */
.img-actions {
  position: absolute;
  top: 3px;
  right: 3px;
  display: none;
  flex-direction: column;
  gap: 3px;
}
.reorder-item:hover .img-actions { display: flex; }

.img-btn {
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 4px;
  font-size: 0.65rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
.thumb-btn  { background: var(--accent);  color: var(--text-primary); opacity: 0.85; }
.delete-btn { background: var(--danger);  color: var(--text-primary); opacity: 0.85; }
.img-btn:hover { opacity: 1; transform: scale(1.08); }

.thumb-indicator {
  position: absolute;
  bottom: 3px;
  left: 5px;
  font-size: 0.65rem;
  pointer-events: none;
}

/* Inline delete confirmation overlay */
.delete-confirm {
  position: absolute;
  inset: 0;
  background: var(--bg-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  font-size: 0.7rem;
  border-radius: var(--radius-sm);
  flex-wrap: wrap;
  padding: 0.25rem;
}
.delete-confirm span { color: var(--text-primary); font-weight: 600; width: 100%; text-align: center; }
.btn-confirm-yes { background: var(--danger);           color: var(--text-primary); border: none; border-radius: 4px; padding: 2px 8px; cursor: pointer; font-size: 0.72rem; }
.btn-confirm-no  { background: var(--bg-surface-hover); color: var(--text-primary); border: none; border-radius: 4px; padding: 2px 8px; cursor: pointer; font-size: 0.72rem; }

/* Download section in Edit modal */
.download-section {
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  background: var(--bg-surface);
}

/* ── Clickable gallery card (G2) ── */
.gv-card-main { cursor: pointer; border-radius: var(--radius-sm); transition: opacity 0.12s; min-width: 0; }
.gv-card-main:hover { opacity: 0.92; }
.gv-card-main:focus-visible { outline: 2px solid var(--accent); outline-offset: 3px; }

/* ── Responsive card grid (dense tiles for hundreds of galleries) ── */
.gv-grid-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 0.85rem;
  align-items: start;
}
.card.gv-tile {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 0.85rem 0.65rem;
  justify-content: flex-start;
  align-items: stretch;
}
.gv-tile .gv-card-main { display: flex; flex-direction: column; gap: 0.3rem; }
/* Polaroid stack sits prominently at the top of each tile. */
.gv-tile .pstack { margin-bottom: 0.15rem; }
.gv-tile-head {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 0.5rem;
}
.gv-tile-name {
  font-size: 0.95rem; line-height: 1.25; min-width: 0;
  overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
}
.gv-tile-head .tier-badge { flex: 0 0 auto; }
.gv-tile-sub { margin: 0; font-size: 0.8rem; color: var(--text-secondary); }
.gv-tile-desc {
  color: var(--text-muted); font-size: 0.76rem; line-height: 1.35;
  overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
}
/* Compact footer actions: small buttons that wrap within the tile. */
.gv-tile-actions {
  display: flex; flex-wrap: wrap; gap: 0.4rem;
  margin-top: auto; padding-top: 0.15rem;
}
.gv-tile-actions .btn-sm { flex: 1 1 auto; min-width: 0; white-space: nowrap; }

/* ── Select Images modal (G4): wider two-column layout + clear marking ── */
.select-modal { max-width: 1040px; width: 94vw; }
.select-body {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
  gap: 1.25rem;
  align-items: start;
}
.select-col-head { margin: 0 0 0.6rem; font-size: 0.9rem; color: var(--text-secondary); }
.chosen-col .reorder-grid { max-height: 62vh; }

.picker-col {
  display: flex; flex-direction: column; gap: 0.5rem;
  max-height: 66vh; overflow-y: auto; padding-right: 0.25rem;
}
.picker-search { width: 100%; min-width: 0; position: sticky; top: 0; z-index: 1; }
.picker-shoot { border: 1px solid var(--border-subtle); border-radius: var(--radius-sm); overflow: hidden; }
.picker-head {
  display: flex; align-items: center; gap: 0.5rem; width: 100%;
  padding: 0.55rem 0.7rem; background: var(--bg-surface); color: var(--text-primary);
  border: none; cursor: pointer; font-size: 0.85rem; text-align: left;
}
.picker-head:hover { background: var(--bg-surface-hover); }
.picker-head.open { background: var(--bg-surface-hover); }
.picker-caret { color: var(--text-muted); flex: 0 0 auto; width: 0.9rem; }
.picker-name { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.picker-count { flex: 0 0 auto; color: var(--text-muted); font-size: 0.75rem; font-variant-numeric: tabular-nums; }
.picker-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
  gap: 0.4rem; padding: 0.5rem; background: var(--bg-elevated);
}
.picker-fig {
  position: relative; margin: 0; border-radius: var(--radius-sm); overflow: hidden;
  cursor: pointer; aspect-ratio: 1 / 1; background: var(--bg-surface);
  border: 2px solid transparent; transition: border-color 0.12s, transform 0.12s;
}
.picker-fig:hover { transform: scale(1.03); border-color: var(--border-focus); }
.picker-fig img { width: 100%; height: 100%; object-fit: cover; display: block; }
.picker-fig.chosen { border-color: var(--accent); }
.picker-fig.chosen img { opacity: 0.55; }
.picker-check {
  position: absolute; top: 3px; right: 3px;
  background: var(--accent); color: var(--text-primary);
  width: 1.1rem; height: 1.1rem; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.7rem; font-weight: 700; line-height: 1;
}
.no-thumb-sm {
  display: flex; align-items: center; justify-content: center;
  width: 100%; height: 100%; min-height: 60px;
  color: var(--text-muted); font-size: 0.8rem; background: var(--bg-elevated);
}
.del-btn {
  position: absolute; top: 3px; right: 3px;
  background: var(--danger); color: var(--text-primary);
  width: 20px; height: 20px; border: none; border-radius: 4px;
  cursor: pointer; font-size: 0.65rem; opacity: 0.85;
}
.del-btn:hover { opacity: 1; }

@media (max-width: 760px) {
  .select-body { grid-template-columns: 1fr; }
}

/* ── Access & Sharing modal (G5) ── */
.acc-block { margin-bottom: 1.25rem; }
.acc-block + .acc-block { border-top: 1px solid var(--border-subtle); padding-top: 1rem; }
.acc-row { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; }
.acc-row-text { display: flex; align-items: center; gap: 0.6rem; }
.acc-label { font-weight: 600; font-size: 0.9rem; }
.acc-hint { display: block; color: var(--text-muted); font-size: 0.78rem; margin-top: 0.35rem; line-height: 1.4; }
.acc-share-toggle { display: flex; gap: 0.6rem; align-items: flex-start; cursor: pointer; }
.acc-share-toggle input { margin-top: 0.2rem; }
.share-link-row { display: flex; gap: 0.5rem; margin-top: 0.6rem; }
.share-link-row .gv-search { flex: 1; min-width: 0; }

/* ── Client combobox (G1) ── */
.gv-client-search { position: relative; flex: 1; min-width: 0; }
.gv-client-input {
  width: 100%; min-width: 0; padding: 0.55rem 2rem 0.55rem 0.75rem;
  border: 1px solid var(--border-default); border-radius: var(--radius-sm);
  background: var(--bg-input); color: var(--text-primary); font-size: 0.9rem;
}
.gv-client-input:focus { outline: none; border-color: var(--border-focus); }
.gv-client-input::placeholder { color: var(--text-secondary); }
.gv-client-clear {
  position: absolute; right: 0.4rem; top: 50%; transform: translateY(-50%);
  background: transparent; border: none; color: var(--text-muted);
  cursor: pointer; font-size: 0.8rem; padding: 0.2rem;
}
.gv-client-clear:hover { color: var(--text-primary); }
.gv-client-results {
  position: absolute; z-index: 20; top: calc(100% + 2px); left: 0; right: 0;
  margin: 0; padding: 0.25rem; list-style: none;
  background: var(--bg-surface); border: 1px solid var(--border-default);
  border-radius: var(--radius-sm); max-height: 260px; overflow-y: auto;
  box-shadow: 0 6px 20px rgba(0,0,0,0.35);
}
.gv-client-results li {
  display: flex; justify-content: space-between; gap: 0.75rem; align-items: baseline;
  padding: 0.45rem 0.6rem; border-radius: var(--radius-sm);
  cursor: pointer; font-size: 0.88rem; color: var(--text-primary);
}
.gv-client-results li:hover,
.gv-client-results li.active { background: var(--bg-surface-hover); }
.gv-client-email { color: var(--text-muted); font-size: 0.75rem; flex: 0 0 auto; }
.gv-client-noresult {
  position: absolute; z-index: 20; top: calc(100% + 2px); left: 0; right: 0; margin: 0;
  padding: 0.5rem 0.6rem; background: var(--bg-surface);
  border: 1px solid var(--border-default); border-radius: var(--radius-sm);
  color: var(--text-muted); font-size: 0.8rem;
}
</style>

